import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HardHat, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0B192C]">
        <img
          src="https://images.unsplash.com/photo-1530639834082-05bafb67fbbe?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
          alt="Construction site" className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="w-12 h-12 rounded bg-[#E8791A] flex items-center justify-center mb-4">
            <HardHat className="w-7 h-7" />
          </div>
          <h1 className="font-head text-4xl font-bold tracking-wide">CHARLES SERVICES</h1>
          <p className="text-lg text-[#9DB4CE] font-head">Labor Productivity Command Center</p>
          <p className="text-sm text-slate-400 mt-4 max-w-md">
            Compare budgeted vs. actual labor, track earned hours and productivity factors, and
            forecast labor-hour gains and losses across every project.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#F8FAFC]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded bg-[#E8791A] flex items-center justify-center">
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <span className="font-head font-bold text-xl">CHARLES SERVICES</span>
          </div>
          <h2 className="font-head text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mb-6">Access your productivity dashboards.</p>

          {error && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                 data-testid="login-error">{error}</div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required data-testid="login-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required data-testid="login-password-input" className="mt-1" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full mt-6 bg-[#0B192C] hover:bg-[#142843] text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Sign in
          </Button>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-sm text-[#E8791A] hover:underline"
                  data-testid="forgot-password-link">Forgot password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
